import React, { createContext, useContext, useState } from "react";
import {
  FunctionField,
  List,
  TextField as RaTextField,
  useRecordContext,
  useSidebarState,
  useTranslate,
} from "react-admin";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import {
  CustomDatagrid,
  FilterField,
  FILTER_HEIGHT,
  ListActionsBar,
  NamedDeleteButton,
} from "@/components/common";
import { useCanCreate, useCanEdit, useCanDelete } from "@/utils/permissions";
import type { Provider } from "../types/provider";
import ProviderFormDialog from "./ProviderFormDialog";

const i18nBase = "resources.providers";

interface RowActionContextValue {
  openEdit: (record: Provider) => void;
}

const RowActionContext = createContext<RowActionContextValue | null>(null);
const useRowActions = (): RowActionContextValue => {
  const ctx = useContext(RowActionContext);
  if (!ctx) {
    throw new Error("Row action used outside ProvidersList");
  }
  return ctx;
};

const EditRowButton: React.FC = () => {
  const record = useRecordContext<Provider>();
  const translate = useTranslate();
  const { openEdit } = useRowActions();
  if (!record) return null;
  return (
    <Button
      size="small"
      variant="text"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={(e) => {
        e.stopPropagation();
        openEdit(record);
      }}
      sx={{ textTransform: "none", fontWeight: 500, minWidth: 0 }}
    >
      {translate("ra.action.edit")}
    </Button>
  );
};

interface ListActionsProps {
  onAdd: () => void;
}

const ProvidersListActions: React.FC<ListActionsProps> = ({ onAdd }) => {
  const translate = useTranslate();
  const canCreate = useCanCreate();
  return (
    <ListActionsBar
      filters={
        <>
          <FilterField
            source="providerName"
            label={translate(`${i18nBase}.filters.providerName`)}
          />
          <FilterField
            source="communicationChannel"
            label={translate(`${i18nBase}.filters.communicationType`)}
          />
        </>
      }
      addButton={
        canCreate ? (
          <Button
            variant="contained"
            color="primary"
            onClick={onAdd}
            startIcon={<AddIcon />}
            aria-label={translate(`${i18nBase}.actions.addProvider`)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              flexShrink: 0,
              height: FILTER_HEIGHT,
              minWidth: { xs: FILTER_HEIGHT, sm: "auto" },
              borderRadius: 1.5,
              px: { xs: 0, sm: 2.5 },
              boxShadow: "none",
              "& .MuiButton-startIcon": {
                mr: { xs: 0, sm: 1 },
                ml: { xs: 0, sm: -0.5 },
              },
              "&:hover": { boxShadow: "0 2px 6px rgba(11, 165, 183, 0.25)" },
            }}
          >
            <Box
              component="span"
              sx={{ display: { xs: "none", sm: "inline" } }}
            >
              {translate(`${i18nBase}.actions.addProvider`)}
            </Box>
          </Button>
        ) : null
      }
    />
  );
};

export const ProvidersList: React.FC = () => {
  const translate = useTranslate();
  const [sidebarOpen] = useSidebarState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const maxWidth = isMobile ? "100%" : sidebarOpen ? "85vw" : "95vw";
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const canEdit = useCanEdit();
  const canDelete = useCanDelete();

  const openEdit = (record: Provider): void => {
    setEditingId(record.id);
  };

  const openAdd = (): void => {
    setCreateOpen(true);
  };

  const handlers: RowActionContextValue = { openEdit };

  return (
    <RowActionContext.Provider value={handlers}>
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            mb: 1,
            mt: 0,
            ml: 2,
          }}
        >
          {translate(`${i18nBase}.name`, { smart_count: 2 })}
        </Typography>
        <List
          actions={<ProvidersListActions onAdd={openAdd} />}
          sort={{ field: "0", order: "ASC" }}
          storeKey={false}
          disableSyncWithLocation
          perPage={10}
          exporter={false}
          sx={{
            maxWidth,
            overflow: "visible",
            "& .RaList-main, & .RaList-content": {
              overflow: "visible",
              width: "100%",
              maxWidth: "100%",
            },
            "& .RaListToolbar-root, & .RaList-actions": {
              display: "flex",
              width: "100%",
              mb: 2,
              px: 0,
              py: { xs: 0.5, sm: 1 },
              minHeight: "auto",
            },
          }}
        >
          <CustomDatagrid
            primarySource="providerName"
            secondarySource="providerStatus"
            detailColumns={2}
            bulkActionButtons={false}
            disableMobileView
          >
            <RaTextField
              source="providerName"
              label={translate(`${i18nBase}.fields.providerName`)}
            />
            <FunctionField<Provider>
              source="communicationType"
              label={translate(`${i18nBase}.fields.communicationType`)}
              render={(record) =>
                Array.isArray(record?.communicationType)
                  ? record.communicationType.join(", ")
                  : ""
              }
            />
            <FunctionField<Provider>
              source="providerStatus"
              label={translate(`${i18nBase}.fields.providerStatus`)}
              render={(record) =>
                record?.providerStatus
                  ? translate(`${i18nBase}.status.${record.providerStatus}`)
                  : ""
              }
            />
            <RaTextField
              source="priority"
              label={translate(`${i18nBase}.fields.priority`)}
            />
            <RaTextField
              source="retries"
              label={translate(`${i18nBase}.fields.retries`)}
            />
            {canEdit && <EditRowButton />}
            {canDelete && <NamedDeleteButton displayField="providerName" />}
          </CustomDatagrid>
        </List>

        <ProviderFormDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          recordId={null}
        />
        <ProviderFormDialog
          open={editingId !== null}
          onClose={() => setEditingId(null)}
          recordId={editingId}
        />
      </Box>
    </RowActionContext.Provider>
  );
};

export default ProvidersList;
